import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import SignaturePad from 'signature_pad';

/*
 * Servicio FERROVIAL RAILWAY
 *  Inicio: Datos del servicio -> F04 (CMM) -> CMR -> CIS -> firma y envío
 *  Fin:    CFS -> envío (reutiliza la firma del inicio)
 * Los datos se guardan en el móvil (localStorage) mientras se rellenan.
 */

const LS_KEY = 'servicios_ferrovial';
const LS_CONFIG = 'ferrovial_config';
const RUTA_INICIO = '/maquinista/nuevo-servicio';

const getLista = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch { return []; } };
const saveLista = l => localStorage.setItem(LS_KEY, JSON.stringify(l));

const ahoraLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};
const aTextoPdf = v => {
  if (!v) return '';
  const [f, h] = v.split('T');
  const [y, m, d] = f.split('-');
  return `${d}/${m}/${y} ${h}`;
};

const S = {
  page: { maxWidth: 600, margin: '0 auto', padding: '16px', fontFamily: 'system-ui, sans-serif' },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #e5e5e5', padding: 16, marginBottom: 16 },
  label: { display: 'block', fontSize: 12, color: '#555', marginBottom: 4 },
  input: { width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' },
  btnPrim: { flex: 1, padding: '14px', borderRadius: 12, border: 'none', background: '#1a1a1a', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  btnSec: { flex: 1, padding: '14px', borderRadius: 12, border: '1px solid #ddd', background: '#fff', color: '#333', fontSize: 15, fontWeight: 500, cursor: 'pointer' },
  seccion: { fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.4, margin: '18px 0 6px' },
  link: { background: 'none', border: 'none', color: '#2563eb', fontSize: 12, cursor: 'pointer', padding: 0 },
};

// ---------------------------------------------------------------------------
// Componentes base
// ---------------------------------------------------------------------------

function Cabecera({ titulo, subtitulo, onVolver }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
      <button onClick={onVolver} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 14 }}>←</button>
      <div style={{ flex: 1 }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{titulo}</h1>
        {subtitulo && <p style={{ fontSize: 12, color: '#888', margin: '2px 0 0' }}>{subtitulo}</p>}
      </div>
    </div>
  );
}

function Campo({ label, children, style }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  );
}

function Texto({ value, onChange, rows, ...rest }) {
  if (rows) return <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} style={{ ...S.input, resize: 'none' }} {...rest} />;
  return <input value={value ?? ''} onChange={e => onChange(e.target.value)} style={S.input} {...rest} />;
}

function Check({ label, value, onChange }) {
  return (
    <button onClick={() => onChange(!value)} style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '12px',
      borderRadius: 10, marginBottom: 10, cursor: 'pointer', fontSize: 14, lineHeight: 1.4,
      border: `2px solid ${value ? '#3b6d11' : '#e5e5e5'}`, background: value ? '#eaf3de' : '#fff', color: '#333'
    }}>
      <span style={{
        width: 26, height: 26, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `2px solid ${value ? '#3b6d11' : '#ccc'}`, background: value ? '#3b6d11' : '#fff', color: '#fff', fontSize: 15
      }}>{value ? '✓' : ''}</span>
      {label}
    </button>
  );
}

function SiNo({ value, onChange, textoSi = 'Sí', textoNo = 'No' }) {
  const b = (activo, color, fondo) => ({
    flex: 1, padding: '10px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
    border: `2px solid ${activo ? color : '#ddd'}`, background: activo ? fondo : '#fff', color: activo ? color : '#555'
  });
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button onClick={() => onChange(true)} style={b(value === true, '#3b6d11', '#eaf3de')}>{textoSi}</button>
      <button onClick={() => onChange(false)} style={b(value === false, '#a32d2d', '#fcebeb')}>{textoNo}</button>
    </div>
  );
}

function Autocompletar({ value, onChange, buscar, minChars = 3, placeholder, onElegir }) {
  const [ops, setOps] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const t = useRef();
  const cambiar = v => {
    onChange(v);
    clearTimeout(t.current);
    if ((v || '').trim().length < minChars) { setOps([]); setAbierto(false); return; }
    t.current = setTimeout(() => {
      buscar(v).then(r => { setOps(r); setAbierto(true); }).catch(() => setOps([]));
    }, 250);
  };
  return (
    <div style={{ position: 'relative' }}>
      <input value={value || ''} placeholder={placeholder} style={S.input}
        onChange={e => cambiar(e.target.value)}
        onFocus={() => ops.length && setAbierto(true)}
        onBlur={() => setTimeout(() => setAbierto(false), 150)} />
      {abierto && ops.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 8, marginTop: 4, zIndex: 50, maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          {ops.map((o, i) => (
            <div key={i} onMouseDown={() => { onChange(o.valor); onElegir && onElegir(o); setAbierto(false); }}
              style={{ padding: '10px 12px', fontSize: 14, cursor: 'pointer', borderBottom: '1px solid #f3f3f3' }}>
              {o.texto}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const buscarEstaciones = q => api.get('/ferrovial/estaciones', { params: { q } })
  .then(r => r.data.map(e => ({ valor: e.nombre, texto: e.nombre })));
const buscarLocos = q => api.get('/ferrovial/locomotoras', { params: { q } })
  .then(r => r.data.map(l => ({ valor: l.uic, texto: `${l.uic} · ${l.numero}` })));

function Navegacion({ onAnterior, onSiguiente, textoSiguiente = 'Siguiente →' }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 24 }}>
      {onAnterior && <button onClick={onAnterior} style={S.btnSec}>← Anterior</button>}
      <button onClick={onSiguiente} style={S.btnPrim}>{textoSiguiente}</button>
    </div>
  );
}

// Pregunta Sí / No / Observación (CIS)
function ItemSiNo({ label, value, onChange }) {
  const v = value || {};
  const [verObs, setVerObs] = useState(!!v.obs);
  const b = (activo, color, fondo) => ({
    width: 52, height: 40, borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    border: `2px solid ${activo ? color : '#ddd'}`, background: activo ? fondo : '#fff', color: activo ? color : '#777'
  });
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, fontSize: 14, lineHeight: 1.4, color: '#333' }}>{label}</div>
        <button onClick={() => onChange({ ...v, valor: 'si' })} style={b(v.valor === 'si', '#3b6d11', '#eaf3de')}>Sí</button>
        <button onClick={() => { onChange({ ...v, valor: 'no' }); setVerObs(true); }} style={b(v.valor === 'no', '#a32d2d', '#fcebeb')}>No</button>
      </div>
      {verObs
        ? <input value={v.obs || ''} onChange={e => onChange({ ...v, obs: e.target.value })} placeholder="Observación"
            style={{ ...S.input, marginTop: 8, padding: '8px 10px', fontSize: 13 }} />
        : <button onClick={() => setVerObs(true)} style={{ ...S.link, marginTop: 4 }}>+ observación</button>}
    </div>
  );
}

// Elemento Correcto / Incorrecto / No aplica + vehículos afectados + observaciones (F04 y CMR)
function ItemEstado({ label, defecto, value, onChange, placeholderVa }) {
  const v = value || {};
  const [verMas, setVerMas] = useState(!!(v.obs || v.va));
  const mostrar = verMas || v.estado === 'noapto';
  const b = (activo, color, fondo) => ({
    minWidth: 44, height: 40, padding: '0 8px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    border: `2px solid ${activo ? color : '#ddd'}`, background: activo ? fondo : '#fff', color: activo ? color : '#777'
  });
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: '#333', lineHeight: 1.3 }}>{label}</div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{defecto}</div>
        </div>
        <button onClick={() => onChange({ ...v, estado: 'apto' })} style={b(v.estado === 'apto', '#3b6d11', '#eaf3de')}>✓</button>
        <button onClick={() => onChange({ ...v, estado: 'noapto' })} style={b(v.estado === 'noapto', '#a32d2d', '#fcebeb')}>✗</button>
        <button onClick={() => onChange({ ...v, estado: 'na' })} style={b(v.estado === 'na', '#555', '#f0f0f0')}>N/A</button>
      </div>
      {mostrar ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8, marginTop: 8 }}>
          <input value={v.va || ''} onChange={e => onChange({ ...v, va: e.target.value })} placeholder={placeholderVa || 'Vehículos afectados'}
            style={{ ...S.input, padding: '8px 10px', fontSize: 13 }} />
          <input value={v.obs || ''} onChange={e => onChange({ ...v, obs: e.target.value })} placeholder="Observaciones"
            style={{ ...S.input, padding: '8px 10px', fontSize: 13 }} />
        </div>
      ) : (
        <button onClick={() => setVerMas(true)} style={{ ...S.link, marginTop: 4 }}>+ observación</button>
      )}
    </div>
  );
}

// Tabla de anomalías (anomalía, detalle, limitaciones, observaciones)
function FilasAnomalias({ filas, onChange, max }) {
  const set = (i, k, val) => onChange(filas.map((f, j) => (j === i ? { ...f, [k]: val } : f)));
  return (
    <div>
      {filas.map((f, i) => (
        <div key={i} style={{ border: '1px solid #eee', borderRadius: 10, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <input value={f.anomalia || ''} onChange={e => set(i, 'anomalia', e.target.value)} placeholder="Anomalía" style={S.input} />
            <input value={f.detalle || ''} onChange={e => set(i, 'detalle', e.target.value)} placeholder="Detalle" style={S.input} />
            <input value={f.limitaciones || ''} onChange={e => set(i, 'limitaciones', e.target.value)} placeholder="Limitaciones" style={S.input} />
            <input value={f.observaciones || ''} onChange={e => set(i, 'observaciones', e.target.value)} placeholder="Observaciones" style={S.input} />
          </div>
          <button onClick={() => onChange(filas.filter((_, j) => j !== i))} style={{ ...S.link, color: '#a32d2d' }}>Quitar</button>
        </div>
      ))}
      {filas.length < max && (
        <button onClick={() => onChange([...filas, {}])} style={{ ...S.btnSec, flex: 'none', width: '100%', padding: '10px', fontSize: 13 }}>
          + Añadir anomalía
        </button>
      )}
    </div>
  );
}

const filasConDatos = filas => (filas || []).filter(f => Object.values(f).some(x => (x || '').toString().trim()));

// ---------------------------------------------------------------------------
// Paso 1: datos del servicio (se preguntan una sola vez)
// ---------------------------------------------------------------------------

function FormDatos({ inicial, onGuardar, onVolver }) {
  const [d, setD] = useState(inicial || {});
  const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));

  const guardar = async () => {
    if (!d.uic1 || !d.datos_servicio || !d.ubicacion) {
      alert('Rellena al menos la locomotora, el nº de tren / EBV y la ubicación'); return;
    }
    for (const uic of [d.uic1, d.uic2].filter(Boolean)) {
      try {
        const r = await api.get('/ferrovial/locomotora', { params: { uic } });
        if (!r.data.existe && !window.confirm(`El UIC ${uic} no está en la base de datos.\n¿Seguro que es correcto?`)) return;
      } catch { /* sin conexión: se deja pasar */ }
    }
    onGuardar(d);
  };

  return (
    <div style={S.page}>
      <Cabecera titulo="Datos del servicio" subtitulo="Se usan en todos los documentos" onVolver={onVolver} />
      <div style={S.card}>
        <Campo label="Locomotora o vehículo motor (UIC)">
          <Autocompletar value={d.uic1} onChange={v => set('uic1', v)} buscar={buscarLocos} minChars={2} placeholder="Escribe el UIC o el número" />
        </Campo>
        <Campo label="Segunda locomotora (si van dos)">
          <Autocompletar value={d.uic2} onChange={v => set('uic2', v)} buscar={buscarLocos} minChars={2} placeholder="Opcional" />
        </Campo>
        <Campo label="Nombre y apellidos del acompañante">
          <Texto value={d.acompanante} onChange={v => set('acompanante', v)} placeholder="Opcional" />
        </Campo>
        <Campo label="Datos del servicio ferroviario (nº de tren o EBV)">
          <Texto value={d.datos_servicio} onChange={v => set('datos_servicio', v)} />
        </Campo>
        <Campo label="Ubicación desde donde comunicas con el CGS 24H">
          <Autocompletar value={d.ubicacion} onChange={v => set('ubicacion', v)} buscar={buscarEstaciones} placeholder="Escribe al menos 3 letras" />
        </Campo>
        <Campo label="Dotación del personal de conducción y, en su caso, de operaciones de tren">
          <Texto value={d.dotacion_personal} onChange={v => set('dotacion_personal', v)} />
        </Campo>
      </div>
      <Navegacion onSiguiente={guardar} textoSiguiente="Guardar datos" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paso 2: F04 Lista chequeo material motor
// ---------------------------------------------------------------------------

function FormCMM({ config, comun, inicial, onGuardar, onVolver }) {
  const init = inicial || {};
  const todos = config.flatMap(s => s.items);
  const [items, setItems] = useState(init.items || {});
  const [medidas, setMedidas] = useState(init.medidas ||
    [comun.uic1, comun.uic2].filter(Boolean).map(uic => ({ uic })));
  const [anomalias, setAnomalias] = useState(init.anomalias || []);
  const [h24, setH24] = useState(init.comunicado_h24 ?? null);
  const [marcha, setMarcha] = useState(init.anomalias_marcha ?? null);
  const [marchaLista, setMarchaLista] = useState(init.anomalias_marcha_lista || []);
  const [obs, setObs] = useState(init.observaciones_generales || '');
  const [fh, setFh] = useState(init.fh_local || ahoraLocal());

  const marcados = todos.filter(i => items[i.clave]?.estado).length;
  const hayNoApto = todos.some(i => items[i.clave]?.estado === 'noapto');
  const setMedida = (i, k, v) => setMedidas(medidas.map((m, j) => (j === i ? { ...m, [k]: v } : m)));

  const todoCorrecto = () => {
    const n = { ...items };
    todos.forEach(i => { if (!n[i.clave]?.estado) n[i.clave] = { ...(n[i.clave] || {}), estado: 'apto' }; });
    setItems(n);
  };

  const guardar = () => {
    if (marcados < todos.length) { alert(`Faltan ${todos.length - marcados} elementos por revisar`); return; }
    if (h24 === null) { alert('Indica si has comunicado al CGS 24H'); return; }
    if (marcha === null) { alert('Indica si existen anomalías que imposibiliten el inicio de la marcha'); return; }
    onGuardar({
      items,
      medidas: hayNoApto ? medidas : [],
      anomalias: filasConDatos(anomalias),
      comunicado_h24: h24,
      anomalias_marcha: marcha,
      anomalias_marcha_lista: marcha ? filasConDatos(marchaLista) : [],
      observaciones_generales: obs,
      fecha_hora: aTextoPdf(fh),
      fh_local: fh,
    });
  };

  return (
    <div style={S.page}>
      <Cabecera titulo="Chequeo material motor (F04)" onVolver={onVolver}
        subtitulo={<>{marcados}/{todos.length} elementos{hayNoApto && <span style={{ color: '#a32d2d' }}> · hay incorrectos</span>}</>} />

      <button onClick={todoCorrecto} style={{ ...S.btnSec, width: '100%', marginBottom: 8, padding: '10px', fontSize: 13 }}>
        Marcar como correctos los que faltan
      </button>

      {config.map(sec => (
        <div key={sec.seccion}>
          <div style={S.seccion}>{sec.seccion}</div>
          <div style={{ ...S.card, padding: '0 16px', marginBottom: 8 }}>
            {sec.items.map(it => (
              <ItemEstado key={it.clave} label={it.etiqueta} defecto={it.defecto} value={items[it.clave]}
                placeholderVa={comun.uic2 ? 'UIC afectado' : comun.uic1}
                onChange={v => setItems(prev => ({ ...prev, [it.clave]: v }))} />
            ))}
          </div>
        </div>
      ))}

      {hayNoApto && (
        <>
          <div style={S.seccion}>Medidas adoptadas</div>
          <div style={S.card}>
            {medidas.map((m, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <Campo label="Nº de locomotora o vehículo motor">
                  <Texto value={m.uic} onChange={v => setMedida(i, 'uic', v)} />
                </Campo>
                <Check label="Retirada del vehículo para mantenimiento" value={!!m.retirada} onChange={v => setMedida(i, 'retirada', v)} />
                <Check label="Reparar al final de su viaje o jornada comercial en una instalación de mantenimiento" value={!!m.reparar} onChange={v => setMedida(i, 'reparar', v)} />
                <Check label="Reparación en siguiente revisión programada o en primera entrada en instalación de mantenimiento" value={!!m.reparar_siguiente} onChange={v => setMedida(i, 'reparar_siguiente', v)} />
              </div>
            ))}
          </div>
        </>
      )}

      <div style={S.seccion}>Anomalías que no imposibilitan la prestación del servicio</div>
      <div style={S.card}><FilasAnomalias filas={anomalias} onChange={setAnomalias} max={3} /></div>

      <div style={S.seccion}>Disponibilidad del vehículo para prestar el servicio</div>
      <div style={S.card}>
        <Campo label="¿Comunicado al CGS 24H? (manual de conducción, esta lista y disponibilidad del vehículo)">
          <SiNo value={h24} onChange={setH24} />
        </Campo>
        <Campo label="¿Existen anomalías que imposibiliten el inicio de la marcha?">
          <SiNo value={marcha} onChange={setMarcha} />
        </Campo>
        {marcha && <FilasAnomalias filas={marchaLista} onChange={setMarchaLista} max={2} />}
      </div>

      <div style={S.card}>
        <Campo label="Observaciones generales"><Texto value={obs} onChange={setObs} rows={3} /></Campo>
        <Campo label="Fecha y hora" style={{ marginBottom: 0 }}>
          <input type="datetime-local" value={fh} onChange={e => setFh(e.target.value)} style={S.input} />
        </Campo>
      </div>

      <Navegacion onSiguiente={guardar} textoSiguiente="Guardar F04" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paso 3: CMR Lista chequeo material remolcado
// ---------------------------------------------------------------------------

function FormCMR({ config, comun, inicial, onGuardar, onVolver }) {
  const init = inicial && !inicial.omitido ? inicial : {};
  const todos = config.flatMap(s => s.items);
  const [d, setD] = useState({ origen: comun.ubicacion, ...init });
  const [items, setItems] = useState(init.items || {});
  const [medida, setMedida] = useState(init.medida || {});
  const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));

  const remolcado = (parseInt(d.coches) || 0) + (parseInt(d.vagones) || 0) > 0;
  const marcados = todos.filter(i => items[i.clave]?.estado).length;
  const noAptos = todos.filter(i => items[i.clave]?.estado === 'noapto');
  const hayNoApto = noAptos.length > 0;

  useEffect(() => {
    if (hayNoApto && !medida.vehiculo) {
      const va = noAptos.map(i => items[i.clave]?.va).find(Boolean);
      if (va) setMedida(m => ({ ...m, vehiculo: va }));
    }
  }, [hayNoApto]); // eslint-disable-line react-hooks/exhaustive-deps

  const todoCorrecto = () => {
    const n = { ...items };
    todos.forEach(i => { if (!n[i.clave]?.estado) n[i.clave] = { ...(n[i.clave] || {}), estado: 'apto' }; });
    setItems(n);
  };

  const guardar = () => {
    if (!remolcado) { onGuardar({ omitido: true }); return; }
    if (marcados < todos.length) { alert(`Faltan ${todos.length - marcados} elementos por revisar`); return; }
    onGuardar({ ...d, items, medida: hayNoApto ? medida : {} });
  };

  return (
    <div style={S.page}>
      <Cabecera titulo="Chequeo material remolcado (CMR)" onVolver={onVolver}
        subtitulo={remolcado ? `${marcados}/${todos.length} elementos` : 'Indica la composición del tren'} />

      <div style={S.card}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <Campo label="Nº coches"><Texto type="number" value={d.coches} onChange={v => set('coches', v)} /></Campo>
          <Campo label="Nº vagones"><Texto type="number" value={d.vagones} onChange={v => set('vagones', v)} /></Campo>
          <Campo label="Nº máq. infra."><Texto type="number" value={d.mv} onChange={v => set('mv', v)} /></Campo>
        </div>
        {!remolcado && (
          <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
            Si no lleva coches ni vagones, este chequeo no hace falta: pulsa «Sin material remolcado».
          </p>
        )}
      </div>

      {remolcado && (
        <>
          <div style={S.seccion}>Servicio ferroviario</div>
          <div style={S.card}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Campo label="Trayecto: inicio">
                <Autocompletar value={d.trayecto_inicio} onChange={v => set('trayecto_inicio', v)} buscar={buscarEstaciones} />
              </Campo>
              <Campo label="Trayecto: fin">
                <Autocompletar value={d.trayecto_fin} onChange={v => set('trayecto_fin', v)} buscar={buscarEstaciones} />
              </Campo>
              <Campo label="Origen">
                <Autocompletar value={d.origen} onChange={v => set('origen', v)} buscar={buscarEstaciones} />
              </Campo>
              <Campo label="Destino">
                <Autocompletar value={d.destino} onChange={v => set('destino', v)} buscar={buscarEstaciones} />
              </Campo>
            </div>
            <Campo label={`Nº de tren: ${comun.datos_servicio || '—'} · observaciones`} style={{ marginBottom: 0 }}>
              <Texto value={d.numero_tren_obs} onChange={v => set('numero_tren_obs', v)} placeholder="Opcional" />
            </Campo>
          </div>

          <button onClick={todoCorrecto} style={{ ...S.btnSec, width: '100%', marginBottom: 8, padding: '10px', fontSize: 13 }}>
            Marcar como correctos los que faltan
          </button>

          {config.map(sec => (
            <div key={sec.seccion}>
              <div style={S.seccion}>{sec.seccion}</div>
              <div style={{ ...S.card, padding: '0 16px', marginBottom: 8 }}>
                {sec.items.map(it => (
                  <ItemEstado key={it.clave} label={it.etiqueta} defecto={it.defecto} value={items[it.clave]}
                    placeholderVa="Coche / vagón"
                    onChange={v => setItems(prev => ({ ...prev, [it.clave]: v }))} />
                ))}
              </div>
            </div>
          ))}

          {hayNoApto && (
            <>
              <div style={S.seccion}>Medidas adoptadas</div>
              <div style={S.card}>
                <Campo label="Nº de coche / vagón / máquina de infraestructura">
                  <Texto value={medida.vehiculo} onChange={v => setMedida({ ...medida, vehiculo: v })} />
                </Campo>
                <Check label="Retirada del vehículo para mantenimiento (segregar el vehículo)" value={!!medida.retirada} onChange={v => setMedida({ ...medida, retirada: v })} />
                <Check label="Reparar al final de su viaje o jornada comercial en una instalación de mantenimiento" value={!!medida.reparar} onChange={v => setMedida({ ...medida, reparar: v })} />
                <Check label="Reparación en siguiente revisión programada o en primera entrada en instalación de mantenimiento" value={!!medida.reparar_siguiente} onChange={v => setMedida({ ...medida, reparar_siguiente: v })} />
              </div>
            </>
          )}

          <div style={S.card}>
            <Campo label="Observaciones generales" style={{ marginBottom: 0 }}>
              <Texto value={d.observaciones_generales} onChange={v => set('observaciones_generales', v)} rows={3} />
            </Campo>
          </div>
        </>
      )}

      <Navegacion onSiguiente={guardar} textoSiguiente={remolcado ? 'Guardar CMR' : 'Sin material remolcado'} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paso 4: CIS Correo inicio de servicio
// ---------------------------------------------------------------------------

function FormCIS({ config, comun, inicial, onGuardar, onVolver }) {
  const init = inicial || {};
  const todos = config.flatMap(s => s.items);
  const [d, setD] = useState(init);
  const [items, setItems] = useState(init.items || {});
  const [fh, setFh] = useState(init.fh_local || ahoraLocal());
  const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));
  const marcados = todos.filter(i => items[i.clave]?.valor).length;

  const todoSi = () => {
    const n = { ...items };
    todos.forEach(i => { if (!n[i.clave]?.valor) n[i.clave] = { ...(n[i.clave] || {}), valor: 'si' }; });
    setItems(n);
  };

  const guardar = () => {
    if (!d.confirmacion && !window.confirm('No has confirmado que estás en perfectas condiciones físicas. ¿Continuar igualmente?')) return;
    if (marcados < todos.length) { alert(`Faltan ${todos.length - marcados} preguntas por responder`); return; }
    onGuardar({ ...d, items, fecha_hora: aTextoPdf(fh), fh_local: fh });
  };

  return (
    <div style={S.page}>
      <Cabecera titulo="Correo inicio de servicio (CIS)" subtitulo={`${marcados}/${todos.length} preguntas`} onVolver={onVolver} />

      <div style={S.seccion}>Datos estado inicial</div>
      <div style={S.card}>
        <p style={{ fontSize: 13, color: '#888', marginTop: 0 }}>Ubicación: <b style={{ color: '#333' }}>{comun.ubicacion}</b></p>
        <Check label="Confirmo que me encuentro en perfectas condiciones físicas para realizar el servicio" value={!!d.confirmacion} onChange={v => set('confirmacion', v)} />
        <Campo label="Identificación de posibles incidencias previas al inicio del servicio">
          <Texto value={d.identificacion} onChange={v => set('identificacion', v)} rows={2} />
        </Campo>
        <Check label="Confirmo la realización del protocolo de toma y deje del vehículo" value={!!d.confirmacion_protocolo} onChange={v => set('confirmacion_protocolo', v)} />
      </div>

      <button onClick={todoSi} style={{ ...S.btnSec, width: '100%', marginBottom: 8, padding: '10px', fontSize: 13 }}>
        Marcar «Sí» en las que faltan
      </button>

      {config.map(sec => (
        <div key={sec.seccion}>
          <div style={S.seccion}>{sec.seccion}</div>
          <div style={{ ...S.card, padding: '0 16px', marginBottom: 8 }}>
            {sec.items.map(it => (
              <ItemSiNo key={it.clave} label={it.etiqueta} value={items[it.clave]}
                onChange={v => setItems(prev => ({ ...prev, [it.clave]: v }))} />
            ))}
          </div>
        </div>
      ))}

      <div style={S.card}>
        <Campo label="Observaciones generales"><Texto value={d.observaciones_generales} onChange={v => set('observaciones_generales', v)} rows={3} /></Campo>
        <Campo label="Fecha y hora (también es la hora de inicio del servicio)" style={{ marginBottom: 0 }}>
          <input type="datetime-local" value={fh} onChange={e => setFh(e.target.value)} style={S.input} />
        </Campo>
      </div>

      <Navegacion onSiguiente={guardar} textoSiguiente="Guardar CIS" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fin: CFS Correo fin de servicio
// ---------------------------------------------------------------------------

function FormCFS({ inicial, onGuardar, onVolver }) {
  const init = inicial || {};
  const [d, setD] = useState({ hora_fin: new Date().toTimeString().slice(0, 5), ...init });
  const [fh, setFh] = useState(init.fh_local || ahoraLocal());
  const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));

  const guardar = () => {
    if (!d.estacion_fin || !d.hora_fin) { alert('Indica el lugar y la hora de finalización'); return; }
    if (!d.tfa && !window.confirm('No has confirmado el vaciado de la TFA y los frenos. ¿Continuar igualmente?')) return;
    onGuardar({ ...d, fecha_hora: aTextoPdf(fh), fh_local: fh });
  };

  return (
    <div style={S.page}>
      <Cabecera titulo="Correo fin de servicio (CFS)" onVolver={onVolver} />
      <div style={S.card}>
        <Campo label="Anomalías detectadas en el funcionamiento de la locomotora y anotadas en el Libro de Reparaciones">
          <Texto value={d.anomalias_libro} onChange={v => set('anomalias_libro', v)} rows={2} />
        </Campo>
        <Campo label="Anomalías detectadas en el material remolcado">
          <Texto value={d.anomalias_remolcado} onChange={v => set('anomalias_remolcado', v)} rows={2} />
        </Campo>
        <Check label="Confirmo haber vaciado completamente la TFA, apretado completamente el freno directo y el freno de estacionamiento" value={!!d.tfa} onChange={v => set('tfa', v)} />
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
          <Campo label="Lugar de finalización">
            <Autocompletar value={d.estacion_fin} onChange={v => set('estacion_fin', v)} buscar={buscarEstaciones} />
          </Campo>
          <Campo label="Hora de finalización">
            <input type="time" value={d.hora_fin || ''} onChange={e => set('hora_fin', e.target.value)} style={S.input} />
          </Campo>
        </div>
        <Campo label="Otras observaciones"><Texto value={d.otras_observaciones} onChange={v => set('otras_observaciones', v)} rows={3} /></Campo>
        <Campo label="Fecha y hora" style={{ marginBottom: 0 }}>
          <input type="datetime-local" value={fh} onChange={e => setFh(e.target.value)} style={S.input} />
        </Campo>
      </div>
      <Navegacion onSiguiente={guardar} textoSiguiente="Guardar CFS" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Firma
// ---------------------------------------------------------------------------

function ModalFirma({ onConfirmar, onCancelar }) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    padRef.current = new SignaturePad(canvasRef.current, { backgroundColor: 'rgba(255,255,255,0)', penColor: 'rgb(0,0,0)' });
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    padRef.current.clear();
  }, []);

  const confirmar = () => {
    if (padRef.current?.isEmpty()) { alert('Por favor firma antes de continuar'); return; }
    onConfirmar(padRef.current.toDataURL('image/png'));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 300 }}>
      <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', padding: 24, width: '100%', maxWidth: 600 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Firma del maquinista</h3>
        <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>Se usará en el F04, el CIS y el CFS</p>
        <div style={{ border: '2px solid #e5e5e5', borderRadius: 10, overflow: 'hidden', marginBottom: 16, background: '#fff', touchAction: 'none' }}>
          <canvas ref={canvasRef} style={{ width: '100%', height: 180, display: 'block', touchAction: 'none' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onCancelar} style={{ ...S.btnSec, padding: 12, fontSize: 14 }}>Cancelar</button>
          <button onClick={() => padRef.current?.clear()} style={{ ...S.btnSec, flex: 'none', padding: '12px 20px', fontSize: 14 }}>Borrar</button>
          <button onClick={confirmar} style={{ ...S.btnPrim, padding: 12, fontSize: 14 }}>Confirmar y enviar</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lista de servicios en curso (ruta sin id)
// ---------------------------------------------------------------------------

function ListaServicios() {
  const navigate = useNavigate();
  const lista = getLista();
  const nuevo = () => {
    const id = Date.now();
    saveLista([...getLista(), { id, creado: new Date().toISOString() }]);
    navigate(`/maquinista/ferrovial/${id}`);
  };
  return (
    <div style={S.page}>
      <Cabecera titulo="Servicios Ferrovial" subtitulo="Inicio y fin de servicio" onVolver={() => navigate(RUTA_INICIO)} />
      <button onClick={nuevo} style={{ ...S.btnPrim, width: '100%', marginBottom: 20 }}>+ Nuevo servicio Ferrovial</button>
      {lista.length > 0 && <div style={S.seccion}>En curso</div>}
      {lista.map(s => (
        <button key={s.id} onClick={() => navigate(`/maquinista/ferrovial/${s.id}`)} style={{
          ...S.card, width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>🚂 {s.comun?.uic1 || 'Sin locomotora'} {s.comun?.datos_servicio ? `· ${s.comun.datos_servicio}` : ''}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              {new Date(s.creado).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })} ·{' '}
              {s.fin_enviado ? 'Finalizado' : s.inicio_enviado ? 'Pendiente de fin de servicio' : 'Inicio sin enviar'}
            </div>
          </div>
          <div style={{ fontSize: 18, color: '#aaa' }}>›</div>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pantalla principal del servicio
// ---------------------------------------------------------------------------

export default function ServicioFerrovial() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [servicio, setServicio] = useState(null);
  const [config, setConfig] = useState(() => { try { return JSON.parse(localStorage.getItem(LS_CONFIG)); } catch { return null; } });
  const [paso, setPaso] = useState(null);
  const [mostrarFirma, setMostrarFirma] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api.get('/ferrovial/config').then(r => {
      setConfig(r.data);
      localStorage.setItem(LS_CONFIG, JSON.stringify(r.data));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    const s = getLista().find(x => x.id === parseInt(id));
    if (!s) { navigate('/maquinista/ferrovial'); return; }
    setServicio(s);
  }, [id, navigate]);

  if (!id) return <ListaServicios />;
  if (!servicio || !config) return <div style={{ padding: 40, color: '#888' }}>Cargando...</div>;

  const actualizar = cambios => {
    const lista = getLista();
    const idx = lista.findIndex(x => x.id === parseInt(id));
    if (idx === -1) return;
    lista[idx] = { ...lista[idx], ...cambios };
    saveLista(lista);
    setServicio(lista[idx]);
  };
  const guardarPaso = clave => datos => { actualizar({ [clave]: datos }); setPaso(null); };
  const limpiar = obj => { if (!obj) return obj; const { fh_local, ...resto } = obj; return resto; };

  const enviarInicio = async firma => {
    setMostrarFirma(false);
    setEnviando(true);
    try {
      const r = await api.post('/ferrovial/inicio', {
        comun: { ...servicio.comun, firma },
        cmm: limpiar(servicio.cmm),
        cmr: servicio.cmr?.omitido ? null : limpiar(servicio.cmr),
        cis: limpiar(servicio.cis),
      });
      actualizar({ inicio_enviado: true, servidor_id: r.data.id, pdfs: r.data.pdfs });
    } catch (e) {
      console.error(e);
      alert('Error al enviar el inicio. Revisa la conexión e inténtalo de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  const enviarFin = async () => {
    setEnviando(true);
    try {
      await api.post(`/ferrovial/${servicio.servidor_id}/fin`, { cfs: limpiar(servicio.cfs) });
      actualizar({ fin_enviado: true, pdfs: [...(servicio.pdfs || []), 'cfs'] });
    } catch (e) {
      console.error(e);
      alert('Error al enviar el fin. Revisa la conexión e inténtalo de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  const verPdf = async formato => {
    try {
      const r = await api.get(`/ferrovial/${servicio.servidor_id}/pdf/${formato}`, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
    } catch { alert('No se ha podido abrir el PDF'); }
  };

  const terminar = () => {
    saveLista(getLista().filter(x => x.id !== parseInt(id)));
    navigate('/maquinista/ferrovial');
  };

  const comun = servicio.comun || {};
  const volver = () => setPaso(null);

  if (paso === 'comun') return <FormDatos inicial={servicio.comun} onGuardar={guardarPaso('comun')} onVolver={volver} />;
  if (paso === 'cmm') return <FormCMM config={config.cmm} comun={comun} inicial={servicio.cmm} onGuardar={guardarPaso('cmm')} onVolver={volver} />;
  if (paso === 'cmr') return <FormCMR config={config.cmr} comun={comun} inicial={servicio.cmr} onGuardar={guardarPaso('cmr')} onVolver={volver} />;
  if (paso === 'cis') return <FormCIS config={config.cis} comun={comun} inicial={servicio.cis} onGuardar={guardarPaso('cis')} onVolver={volver} />;
  if (paso === 'cfs') return <FormCFS inicial={servicio.cfs} onGuardar={guardarPaso('cfs')} onVolver={volver} />;

  const pasosInicio = [
    { key: 'comun', label: 'Datos del servicio' },
    { key: 'cmm', label: 'Chequeo material motor (F04)' },
    { key: 'cmr', label: 'Chequeo material remolcado (CMR)', extra: servicio.cmr?.omitido ? 'Sin material remolcado' : null },
    { key: 'cis', label: 'Correo inicio de servicio (CIS)' },
  ];
  const inicioCompleto = pasosInicio.every(p => servicio[p.key]);
  const nombresPdf = { cmm: 'F04', cmr: 'CMR', cis: 'CIS', cfs: 'CFS' };

  const Tarjeta = ({ num, label, extra, completado, bloqueado, enviado, onClick }) => (
    <button onClick={() => !bloqueado && !enviado && onClick()} disabled={bloqueado || enviado} style={{
      padding: '18px', borderRadius: 14, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 16,
      border: `2px solid ${completado ? '#97c459' : bloqueado ? '#f0f0f0' : '#e5e5e5'}`,
      background: completado ? '#eaf3de' : bloqueado ? '#f8f8f8' : '#fff',
      cursor: bloqueado || enviado ? 'default' : 'pointer'
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: completado ? '#3b6d11' : bloqueado ? '#ddd' : '#f0f0f0', color: completado ? '#fff' : '#888', fontSize: completado ? 18 : 14
      }}>{completado ? '✓' : num}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: bloqueado ? '#aaa' : '#1a1a1a' }}>{label}</div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
          {enviado ? 'Enviado' : extra || (completado ? 'Completado — pulsa para editar' : bloqueado ? 'Completa el paso anterior primero' : 'Pendiente — pulsa para rellenar')}
        </div>
      </div>
      {!bloqueado && !enviado && <div style={{ fontSize: 18, color: '#aaa' }}>›</div>}
    </button>
  );

  return (
    <div style={S.page}>
      <Cabecera titulo={`🚂 ${comun.uic1 || 'Servicio Ferrovial'}${comun.uic2 ? ' / ' + comun.uic2 : ''}`}
        subtitulo={comun.datos_servicio || 'Nuevo servicio'} onVolver={() => navigate('/maquinista/ferrovial')} />

      <div style={S.seccion}>Inicio de servicio</div>
      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        {pasosInicio.map((p, i) => (
          <Tarjeta key={p.key} num={i + 1} label={p.label} extra={p.extra}
            completado={!!servicio[p.key]} enviado={servicio.inicio_enviado}
            bloqueado={i > 0 && !servicio[pasosInicio[i - 1].key]}
            onClick={() => setPaso(p.key)} />
        ))}
      </div>

      {inicioCompleto && !servicio.inicio_enviado && (
        <button onClick={() => setMostrarFirma(true)} disabled={enviando}
          style={{ ...S.btnPrim, width: '100%', padding: 18, fontSize: 16, marginBottom: 16, background: enviando ? '#ccc' : '#1a1a1a' }}>
          {enviando ? 'Enviando...' : 'Firmar y enviar inicio ✓'}
        </button>
      )}

      <div style={S.seccion}>Fin de servicio</div>
      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        <Tarjeta num={5} label="Correo fin de servicio (CFS)"
          completado={!!servicio.cfs} enviado={servicio.fin_enviado}
          bloqueado={!servicio.inicio_enviado}
          extra={!servicio.inicio_enviado ? 'Disponible al enviar el inicio' : null}
          onClick={() => setPaso('cfs')} />
      </div>

      {servicio.cfs && servicio.inicio_enviado && !servicio.fin_enviado && (
        <button onClick={enviarFin} disabled={enviando}
          style={{ ...S.btnPrim, width: '100%', padding: 18, fontSize: 16, marginBottom: 16, background: enviando ? '#ccc' : '#1a1a1a' }}>
          {enviando ? 'Enviando...' : 'Enviar fin de servicio ✓'}
        </button>
      )}

      {servicio.inicio_enviado && servicio.pdfs?.length > 0 && (
        <>
          <div style={S.seccion}>Documentos generados</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {servicio.pdfs.map(f => (
              <button key={f} onClick={() => verPdf(f)} style={{ ...S.btnSec, flex: '1 1 40%', padding: '12px', fontSize: 14 }}>
                📄 {nombresPdf[f] || f}
              </button>
            ))}
          </div>
        </>
      )}

      {servicio.fin_enviado && (
        <button onClick={terminar} style={{ ...S.btnPrim, width: '100%', padding: 16, background: '#3b6d11' }}>
          Servicio finalizado · Cerrar
        </button>
      )}

      {mostrarFirma && <ModalFirma onConfirmar={enviarInicio} onCancelar={() => setMostrarFirma(false)} />}
    </div>
  );
}
